"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { ArrowRight, LayoutGrid, Search, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { isFavoriteTool, toggleFavoriteTool } from "@/lib/favorite-tools";
import {
  assetCategoryToolRank,
  HUB_ASSET_TAGS,
  HUB_ASSET_TILES,
  HUB_CATEGORIES,
  type HubAssetTag,
  type HubAssetTile,
  type HubTool,
} from "./hubData";
import { HubBadge } from "./HubBadge";
import { useServiceSettings } from "@/hooks/useSiteSettings";
import { resolveToolSortOrder } from "@/lib/toolDisplayOrder";
import { resolveToolPreviewImage } from "@/lib/toolPreviewImages";

export function HubAssetDiscovery() {
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState<HubAssetTag>("all");
  const { data: serviceSettings = [] } = useServiceSettings();

  const toolByHref = useMemo(() => {
    const map = new Map<string, HubTool>();
    for (const cat of HUB_CATEGORIES) for (const tool of cat.tools) map.set(tool.href, tool);
    return map;
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return HUB_ASSET_TILES.filter((tile) => {
      if (tag !== "all" && !tile.tags.includes(tag)) return false;
      if (!q) return true;
      return (
        tile.title.toLowerCase().includes(q) ||
        tile.byline.toLowerCase().includes(q)
      );
    }).sort((a, b) => {
      // When a single asset category is selected, honor the owner-curated order
      // (shared with the home + marketing grids). The "all" view keeps the
      // default admin-order → tile-declaration sort.
      if (tag !== "all") {
        const aCur = assetCategoryToolRank(tag, a.href);
        const bCur = assetCategoryToolRank(tag, b.href);
        if (aCur !== bCur) {
          if (aCur === -1) return 1;
          if (bCur === -1) return -1;
          return aCur - bCur;
        }
      }
      const aTool = toolByHref.get(a.href);
      const bTool = toolByHref.get(b.href);
      const aOrder = resolveToolSortOrder({ href: a.href, featureKey: aTool?.featureKey, serviceSettings });
      const bOrder = resolveToolSortOrder({ href: b.href, featureKey: bTool?.featureKey, serviceSettings });
      if (aOrder !== bOrder) return aOrder - bOrder;
      return HUB_ASSET_TILES.indexOf(a) - HUB_ASSET_TILES.indexOf(b);
    });
  }, [query, serviceSettings, tag, toolByHref]);

  return (
    <section className="flex flex-col">
      <header className="flex flex-none items-center gap-2.5">
        <span className="grid h-9 w-9 place-items-center rounded-xl border border-accent/30 bg-accent/10 text-accent">
          <LayoutGrid className="h-4 w-4" />
        </span>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
            Hub
          </p>
          <h2 className="text-lg font-semibold tracking-tight text-foreground">
            What are you building today?
          </h2>
        </div>
      </header>

      <label className="mt-5 flex flex-none items-center gap-2.5 rounded-2xl border border-border/70 bg-secondary/40 px-3.5 py-2.5">
        <Search className="h-4 w-4 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search 3D models, packs, and starting points…"
          className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/70"
        />
      </label>

      <div className="mt-3 flex flex-none flex-wrap items-center gap-1.5">
        {HUB_ASSET_TAGS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => setTag(option.id)}
            className={cn(
              "rounded-full border px-3 py-0.5 text-[11px] transition",
              tag === option.id
                ? "border-accent/45 bg-accent/15 text-foreground"
                : "border-border/70 bg-secondary/30 text-muted-foreground hover:border-accent/30 hover:text-foreground",
            )}
          >
            {option.label}
          </button>
        ))}
        <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {filtered.length} suggested
        </span>
      </div>

      <div className="mt-4 grid auto-rows-min content-start grid-cols-1 gap-3 pr-1 sm:grid-cols-2 xl:grid-cols-3">
        {filtered.length === 0 ? (
          <div className="col-span-full rounded-2xl border border-dashed border-border/70 bg-card/40 px-4 py-12 text-center text-xs text-muted-foreground">
            Nothing matches that yet — clear the filter or try a different search.
          </div>
        ) : (
          filtered.map((tile) => {
            const tool = toolByHref.get(tile.href);
            const imageUrl = resolveToolPreviewImage({
              href: tile.href,
              featureKey: tool?.featureKey,
              serviceSettings,
            });
            return <DiscoveryTile key={tile.id} tile={tile} imageUrl={imageUrl} />;
          })
        )}
      </div>

      <div className="mt-5 flex flex-none items-center justify-between gap-3 rounded-2xl border border-border/70 bg-card/70 px-4 py-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            Heads up
          </p>
          <p className="text-xs text-foreground/85">
            Live community asset feed is coming. Open Model Forge to generate your own in the meantime.
          </p>
        </div>
        <Link
          href="/model-forge"
          className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground shadow-[0_18px_50px_hsl(var(--primary)/0.22)] transition hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-[0_24px_70px_hsl(var(--primary)/0.28)]"
        >
          Open Model Forge
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </section>
  );
}

function DiscoveryTile({ tile, imageUrl }: { tile: HubAssetTile; imageUrl: string | null }) {
  const Icon = tile.icon;
  const [favorited, setFavorited] = useState(false);

  useEffect(() => {
    setFavorited(isFavoriteTool(tile.href));
    const onChange = () => setFavorited(isFavoriteTool(tile.href));
    window.addEventListener("workshop-favorites-changed", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("workshop-favorites-changed", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, [tile.href]);

  return (
    <div className="group relative h-full">
      <Link
        href={tile.href}
        className="group relative block overflow-hidden rounded-2xl border border-border/70 bg-card/70 transition duration-200 hover:-translate-y-0.5 hover:border-accent/40"
      >
        <div className="relative h-32 w-full overflow-hidden">
          {imageUrl ? (
            <>
              <img
                src={imageUrl}
                alt=""
                aria-hidden
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-card/90 to-transparent" />
              <Icon className="absolute left-3 top-3 h-5 w-5 text-white drop-shadow transition group-hover:scale-110" aria-hidden />
            </>
          ) : (
            <>
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(circle at 20% 10%, hsl(var(--accent) / 0.42), transparent 60%), radial-gradient(circle at 90% 100%, hsl(var(--primary) / 0.24), transparent 55%), hsl(var(--surface-2))",
                }}
                aria-hidden
              />
              <Icon
                className="absolute left-3 top-3 h-5 w-5 text-foreground/60 transition group-hover:text-accent"
                aria-hidden
              />
              <Icon
                className="absolute left-1/2 top-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2 text-foreground/85 drop-shadow-[0_10px_24px_hsl(var(--background)/0.55)]"
                aria-hidden
              />
            </>
          )}
        </div>
        <div className="p-3.5">
          <p className="flex items-center gap-1.5 text-sm font-semibold tracking-tight text-foreground">
            <span className="line-clamp-1">{tile.title}</span>
            {tile.badge ? <HubBadge kind={tile.badge} /> : null}
          </p>
          <p className="mt-0.5 line-clamp-1 text-[11px] text-muted-foreground">
            {tile.byline}
          </p>
        </div>
      </Link>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const next = toggleFavoriteTool(tile.href);
          setFavorited(next);
        }}
        aria-label={favorited ? `Remove ${tile.title} from favorites` : `Favorite ${tile.title}`}
        title={favorited ? "Unfavorite" : "Favorite"}
        className={cn(
          "absolute right-2 top-2 z-10 grid h-7 w-7 place-items-center rounded-md transition",
          favorited
            ? "text-amber-400 hover:bg-amber-400/10"
            : "text-muted-foreground/40 opacity-0 hover:bg-muted/40 hover:text-foreground group-hover:opacity-100 focus:opacity-100",
        )}
      >
        <Star className={cn("h-4 w-4", favorited ? "fill-current" : "")} />
      </button>
    </div>
  );
}
